import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from '@/components/icons';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Visible heading. If omitted, pass `ariaLabel` for an accessible name. */
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Accessible name when there's no visible title. */
  ariaLabel?: string;
  children?: React.ReactNode;
  /** Extra classes on the content panel (e.g. a custom max-width). */
  className?: string;
  showClose?: boolean;
}

/**
 * Radix-backed modal — gives focus-trap, ESC-to-close, scroll-lock, and
 * aria-modal semantics that the app's hand-rolled modals lacked. Styled to the
 * design system (bg-popover, tw-animate-css enter/exit).
 */
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  ariaLabel,
  children,
  className,
  showClose = true,
}: ModalProps) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out" />
        <Dialog.Content
          className={`fixed left-1/2 top-1/2 z-[70] w-[min(480px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out ${className ?? ''}`}
        >
          {/* Radix requires a Title for a11y; keep it sr-only when there's none. */}
          <Dialog.Title className={title ? 'px-5 pt-4 text-[15px] font-semibold' : 'sr-only'}>
            {title ?? ariaLabel ?? 'Dialog'}
          </Dialog.Title>
          {description && (
            <Dialog.Description className="px-5 pt-1 text-[13px] text-muted-foreground">
              {description}
            </Dialog.Description>
          )}
          {showClose && (
            <Dialog.Close
              aria-label="Close"
              className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          )}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default Modal;
